import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Globe, Building2, LayoutGrid, List } from "lucide-react";
import api from "../api";
import { useAuth, can } from "../auth";
import { Button, Input, Select, Field, Modal, EmptyState, Spinner, Textarea, ImageWithFallback } from "../components/common";
import { useViewPrefs } from "../hooks/useViewPrefs";
import { groupAndSort, distinctValues } from "../utils/grouping";

const empty = { name: "", industry: "", website: "", phone: "", email: "", address: "", size: "", notes: "" };

const SORT_OPTIONS = ["newest", "oldest", "name_asc", "name_desc", "industry_asc", "size_asc"];
const GROUP_OPTIONS = ["none", "industry", "size"];
const VIEW_PREFS_KEY = "companiesViewPrefs";

const SORT_FNS = {
  name_asc: (a, b) => a.name.localeCompare(b.name),
  name_desc: (a, b) => b.name.localeCompare(a.name),
  oldest: (a, b) => new Date(a.created_at) - new Date(b.created_at),
  industry_asc: (a, b) => (a.industry || "").localeCompare(b.industry || ""),
  size_asc: (a, b) => (a.size || "").localeCompare(b.size || ""),
  newest: (a, b) => new Date(b.created_at) - new Date(a.created_at),
};

export default function Companies() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const writable = can.write(user);
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);

  const [prefs, setPrefs] = useViewPrefs(VIEW_PREFS_KEY,
    { view: ["grid", "list"], sort: SORT_OPTIONS, groupBy: GROUP_OPTIONS },
    { view: "grid", sort: "newest", groupBy: "none" });
  const { view, sort, groupBy } = prefs;

  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterSize, setFilterSize] = useState("");
  const [hasWebsiteOnly, setHasWebsiteOnly] = useState(false);

  const load = useCallback(() => {
    api.get("/companies", { params: { search } }).then((r) => setItems(r.data));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setModal(true); };
  const save = async () => {
    await api.post("/companies", form);
    setModal(false); load();
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const industries = useMemo(() => distinctValues(items || [], (c) => c.industry), [items]);
  const sizes = useMemo(() => distinctValues(items || [], (c) => c.size), [items]);

  const visibleItems = useMemo(() => (items || [])
    .filter((c) => !filterIndustry || c.industry === filterIndustry)
    .filter((c) => !filterSize || c.size === filterSize)
    .filter((c) => !hasWebsiteOnly || !!c.website),
  [items, filterIndustry, filterSize, hasWebsiteOnly]);

  const groups = useMemo(() => groupAndSort(visibleItems, {
    groupBy, groupKeyOf: (c) => c[groupBy], sortFn: SORT_FNS[sort] || SORT_FNS.newest,
    uncategorizedLabel: t("company.uncategorized"),
  }), [visibleItems, groupBy, sort, t]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("company.title")}</h1>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button onClick={() => setPrefs((p) => ({ ...p, view: "grid" }))} data-testid="companies-view-grid"
              className={`p-2 transition-colors ${view === "grid" ? "bg-primary text-white" : "text-muted hover:bg-surface"}`}><LayoutGrid size={16} /></button>
            <button onClick={() => setPrefs((p) => ({ ...p, view: "list" }))} data-testid="companies-view-list"
              className={`p-2 transition-colors ${view === "list" ? "bg-primary text-white" : "text-muted hover:bg-surface"}`}><List size={16} /></button>
          </div>
          {writable && <Button onClick={openNew} data-testid="add-company-btn"><Plus size={16} /><span className="hidden sm:inline">{t("company.newCompany")}</span></Button>}
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input data-testid="company-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} className="pl-9" />
      </div>

      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <div className="w-40 shrink-0">
            <Select aria-label={t("company.sortBy")} data-testid="company-sort" value={sort}
              onChange={(e) => setPrefs((p) => ({ ...p, sort: e.target.value }))}>
              {SORT_OPTIONS.map((s) => <option key={s} value={s}>{t(`company.sort_${s}`)}</option>)}
            </Select>
          </div>
          <div className="w-40 shrink-0">
            <Select aria-label={t("company.groupBy")} data-testid="company-group-by" value={groupBy}
              onChange={(e) => setPrefs((p) => ({ ...p, groupBy: e.target.value }))}>
              <option value="none">{t("company.groupNone")}</option>
              <option value="industry">{t("company.groupByIndustry")}</option>
              <option value="size">{t("company.groupBySize")}</option>
            </Select>
          </div>
          {industries.length > 0 && (
            <div className="w-40 shrink-0">
              <Select aria-label={t("company.industry")} data-testid="company-filter-industry" value={filterIndustry}
                onChange={(e) => setFilterIndustry(e.target.value)}>
                <option value="">{t("company.allIndustries")}</option>
                {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              </Select>
            </div>
          )}
          {sizes.length > 0 && (
            <div className="w-40 shrink-0">
              <Select aria-label={t("company.size")} data-testid="company-filter-size" value={filterSize}
                onChange={(e) => setFilterSize(e.target.value)}>
                <option value="">{t("company.allSizes")}</option>
                {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
          )}
          <Button variant={hasWebsiteOnly ? "primary" : "subtle"} className="py-1.5 px-3 text-xs"
            onClick={() => setHasWebsiteOnly(!hasWebsiteOnly)} data-testid="company-filter-website">
            {t("company.hasWebsite")}
          </Button>
        </div>
      )}

      {!items ? <Spinner /> : items.length === 0 ? (
        <EmptyState title={t("common.noResults")} action={writable && <Button onClick={openNew}><Plus size={16} />{t("company.newCompany")}</Button>} />
      ) : visibleItems.length === 0 ? (
        <EmptyState title={t("common.noResults")} />
      ) : (
        <div className="space-y-6">
          {groups.map((group, gi) => (
            <div key={group.key}>
              {group.label !== null && (
                <div className="flex items-center gap-2 mb-2 px-1">
                  <h3 className="text-xs uppercase tracking-[0.15em] text-muted font-semibold">{group.label}</h3>
                  <span className="text-xs text-muted">({group.items.length})</span>
                </div>
              )}
              {view === "grid" ? (
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${gi === 0 ? "stagger" : ""}`}>
                  {group.items.map((c) => (
                    <div key={c.id} data-testid={`company-card-${c.id}`} onClick={() => navigate(`/companies/${c.id}`)} className="border border-border rounded-sm p-5 hover:-translate-y-px transition-transform duration-200 cursor-pointer">
                      <ImageWithFallback src={c.logo_url} size={40} fallback={<Building2 size={18} />} />
                      <div className="font-display font-bold text-lg mt-3 truncate">{c.name}</div>
                      <div className="text-sm text-muted">{c.industry || "—"}{c.size && ` · ${c.size}`}</div>
                      {c.website && (
                        <a href={`https://${c.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-primary flex items-center gap-1 mt-2 hover:underline">
                          <Globe size={12} /> {c.website}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-border rounded-sm overflow-hidden">
                  {group.items.map((c) => (
                    <div key={c.id} data-testid={`company-row-${c.id}`} onClick={() => navigate(`/companies/${c.id}`)} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-surface transition-colors cursor-pointer">
                      <ImageWithFallback src={c.logo_url} size={28} fallback={<Building2 size={14} />} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted truncate">{c.industry || "—"}{c.size && ` · ${c.size}`}</div>
                      </div>
                      {c.website && (
                        <a href={`https://${c.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hidden sm:flex text-xs text-primary items-center gap-1 hover:underline shrink-0">
                          <Globe size={12} /> {c.website}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={t("company.newCompany")}
        footer={<>
          <Button variant="ghost" onClick={() => setModal(false)}>{t("common.cancel")}</Button>
          <Button onClick={save} data-testid="save-company-btn">{t("common.save")}</Button>
        </>}>
        <Field label={t("company.name")}><Input data-testid="company-name" value={form.name} onChange={set("name")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("company.industry")}><Input value={form.industry || ""} onChange={set("industry")} /></Field>
          <Field label={t("company.size")}><Input value={form.size || ""} onChange={set("size")} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("company.website")}><Input value={form.website || ""} onChange={set("website")} /></Field>
          <Field label={t("company.phone")}><Input value={form.phone || ""} onChange={set("phone")} /></Field>
        </div>
        <Field label={t("company.email")}><Input type="email" value={form.email || ""} onChange={set("email")} /></Field>
        <Field label={t("company.address")}><Input value={form.address || ""} onChange={set("address")} /></Field>
        <Field label={t("company.notes")}><Textarea rows={3} value={form.notes || ""} onChange={set("notes")} /></Field>
      </Modal>
    </div>
  );
}
